<template>
  <div
    :class="skInputContainerClass"
   data-testid="components-ui-sk-input-sk-input-div">
    <div
      :class="skInputClass"
     data-testid="components-ui-sk-input-sk-input-div-2">
      <div :class="skInputWrapperClass" data-testid="components-ui-sk-input-sk-input-div-3">
        <div
          v-if="label && isLargeSize"
          class="sk-input__label"
         data-testid="components-ui-sk-input-sk-input-div-4">
          {{ label }}:
        </div>

        <div class="sk-input__inner" data-testid="components-ui-sk-input-sk-input-div-5">
          <input
            v-model="text"
            :placeholder="resolvedPlaceholder"
            :type="type"
           data-testid="components-ui-sk-input-sk-input-input">
        </div>
      </div>

      <div
        class="sk-input__icon"
        @click="onAppendClick"
       data-testid="components-ui-sk-input-sk-input-div-6">
        <div v-if="isNotSmallSize" data-testid="components-ui-sk-input-sk-input-div-7">
          <slot name="append-icon" />
        </div>
      </div>
    </div>

    <Transition name="sk-hint-fade" data-testid="components-ui-sk-input-sk-input-transition">
      <div
        v-if="errorState || hint"
        class="sk-input__hint"
       data-testid="components-ui-sk-input-sk-input-div-8">
        <template v-if="errorState">
          {{ errorText }}
        </template>

        <template v-if="hint">
          {{ hint }}
        </template>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const emits = defineEmits(['update:modelValue', 'click:append'])

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  size: {
    type: String,
    default: 'lg',
    validator(value) {
      return ['sm', 'md', 'lg'].includes(value)
    }
  },
  type: {
    type: String,
    default: 'text'
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  maxWidth: {
    type: String,
    default: '100%'
  },
  hint: {
    type: String,
    default: ''
  },
  error: {
    type: Boolean,
    default: false
  },
  errorText: {
    type: String,
    default: ''
  },
})

const skInputClass = computed(() => {
  return {
    'sk-input': true,
    [`sk-input-${props.size}`]: true,
    'sk-input--readonly': props.readonly,
    'sk-input--disabled': props.disabled,
  }
})

const skInputWrapperClass = computed(() => {
  return {
    'sk-input__wrapper': true,
    [`sk-input__wrapper-${props.size}`]: true,
  }
})

const skInputContainerClass = computed(() => {
  return {
    'sk-input__container': true,
    'sk-input--error': props.error
  }
})

const isLargeSize = computed(() => props.size === 'lg')

const isNotSmallSize = computed(() => props.size !== 'sm')

const errorState = computed(() => props.error && props.errorText)

const resolvedPlaceholder = computed(() => props.placeholder || t('placeholder.enter_text'))

const text = computed({
  get: () => props.modelValue,
  set: val => {
    emits('update:modelValue', val)
  }
})

const onAppendClick = () => {
  emits('click:append')
}
</script>

<style lang="scss" scoped>
.sk-input {
  outline: none;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  border: 1px solid transparent;
  transition: all 0.3s ease;
  background-color: #edeff3;
  width: 100%;
  max-width: v-bind(maxWidth);
  appearance: none;
  -moz-appearance: none;
  -webkit-appearance: none;
  position: relative;

  &:hover {
    border-color: #dce1e7;
  }

  &__container {
    width: 100%;
    max-width: v-bind(maxWidth);
  }

  &__wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &-sm {
      padding: 4px 8px 4px 8px;
    }

    &-md {
      padding: 5px 9px 5px 8px;
    }

    &-lg {
      padding: 0 9px 0px 5px;
    }
  }

  &-sm {
    min-height: 24px;
    border: 1px solid #dce1e7;
    background-color: #ffffff;
    border-radius: 4px;
    font-size: 14px;
    line-height: 100%;

    &:hover {
      border: 1px solid #cdd4dd;
    }
  }

  &-md {
    min-height: 36px;
    padding: 4px;
    border-radius: 10px;
  }

  &-lg {
    min-height: 48px;
    padding: 6px;
    border-radius: 12px;
    font-size: 16px;
    line-height: 19px;
  }

  &-md,
  &-lg {
    font-size: 16px;
    line-height: 19px;
  }

  &__label {
    font-size: 13px;
    white-space: nowrap;
    color: #292929;
    font-weight: 700;
    line-height: 16px;
  }

  &__inner {
    //height: 19px;
    font-weight: 400;
    color: #292929;
    display: flex;
    //flex-wrap: wrap;
    gap: 6px;
    align-items: center;

    & > input {
      width: 100%;
      background-color: transparent;
      outline: none;

      &::placeholder {
        color: #a6aeb8;
        font-size: 16px;
        line-height: 19px;
        font-family: 'Source Sans Pro';
      }
    }
  }

  &__icon {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-right: 4px;
    gap: 7px;
  }

  &__hint {
    padding-left: 12px;
    margin-top: 3px;
    font-weight: 400;
    font-size: 12px;
    color: #828d9b;
  }

  &--disabled {
    cursor: default;
    background-color: #f6f7fa;
    pointer-events: none;

    &:hover {
      border-color: transparent;
    }

    .sk-input {
      &__inner {
        color: #828d9b;
      }

      &__label {
        color: #828d9b;
      }
    }
  }

  &--readonly {
    cursor: default;
    pointer-events: none;

    &:hover {
      border-color: transparent;

      &.sk-select-sm {
        border-color: #dce1e7;
      }
    }
  }

  &--error {
    .sk-input {
      &__label {
        color: #c93131;
      }

      &__inner {
        color: #c93131;
      }

      &__hint {
        color: #c93131 !important;
      }
    }

    .sk-input-sm {
      border-color: #c93131;

      .sk-input {
        &__inner {
          color: #595959;
        }
      }
    }

    &-hint {
      padding-left: 12px;
      margin-top: 2px;
      font-weight: 400;
      font-size: 12px;
      line-height: 140%;
      color: #c93131;
    }
  }
}

.sk-hint-fade-enter-active,
.sk-hint-fade-leave-active {
  transition: all 0.2s ease;
}
.sk-hint-fade-enter-from,
.sk-hint-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
.sk-hint-fade-enter-to,
.sk-hint-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>